import React from "react"
import { Common } from "lib/types"
import {
  Container,
  Icon,
  Wrapper,
  DropdownContainer,
  DropdownHeader,
  DropdownContent,
  OptionItem,
  OptionLabel,
  ToggleContainer,
  ToggleButton,
  SelectedCount,
  SelectedItems,
  SelectedItem,
  RemoveButton,
  ChevronIcon
} from "./FilterSectionWithToggle.styled"

import { ReactComponent as XMarkIcon } from "images/icons/xmark.svg"

type Props = {
  setValue?: (value: Common.OptionWithToggle[]) => void
  options?: Common.Option[]
  icon?: React.ReactNode
  placeHolder: string
  defaultValue?: Common.OptionWithToggle[]
}

const FilterSectionWithToggle = ({ defaultValue = [], setValue, options = [], icon, placeHolder }: Props) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const handleToggle = (optionValue: string, toggleType: 'include' | 'exclude') => {
    if (!setValue) return

    const existingOption = defaultValue.find(item => item.value === optionValue)
    const otherOptions = defaultValue.filter(item => item.value !== optionValue)

    if (existingOption) {
      if ((toggleType === 'include' && existingOption.include === true) ||
          (toggleType === 'exclude' && existingOption.include === false)) {
        // Remove the option if clicking the same toggle
        setValue(otherOptions)
      } else {
        // Change the toggle state
        const updatedOption = {
          ...existingOption,
          include: toggleType === 'include'
        }
        setValue([...otherOptions, updatedOption])
      }
    } else {
      // Add new option
      const baseOption = options.find(opt => opt.value === optionValue)
      if (baseOption) {
        const newOption: Common.OptionWithToggle = {
          ...baseOption,
          include: toggleType === 'include'
        }
        setValue([...defaultValue, newOption])
      }
    }
  }

  const handleRemoveItem = (optionValue: string) => {
    if (!setValue) return
    const filteredOptions = defaultValue.filter(item => item.value !== optionValue)
    setValue(filteredOptions)
  }

  const renderSelectedContent = () => {
    if (defaultValue.length === 0) {
      return <SelectedCount $isPlaceholder={true}>{placeHolder}</SelectedCount>
    }

    return (
      <SelectedItems>
        {defaultValue.map((item) => (
          <SelectedItem key={item.value} $include={item.include}>
            {item.include ? 'Include' : 'Exclude'} | {item.label}
            <RemoveButton
              onClick={(e) => {
                e.stopPropagation()
                handleRemoveItem(item.value)
              }}
            >
              <XMarkIcon />
            </RemoveButton>
          </SelectedItem>
        ))}
      </SelectedItems>
    )
  }

  const isOptionSelected = (optionValue: string, toggleType: 'include' | 'exclude') => {
    const option = defaultValue.find(item => item.value === optionValue)
    if (!option) return false
    return toggleType === 'include' ? option.include === true : option.include === false
  }

  const isOptionActive = (optionValue: string) => {
    return defaultValue.some(item => item.value === optionValue)
  }

  return (
    <Container>
      <Wrapper>
        {icon && <Icon>{icon}</Icon>}
        <DropdownContainer ref={dropdownRef}>
          <DropdownHeader $isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
            {renderSelectedContent()}
            <ChevronIcon $isOpen={isOpen}>▼</ChevronIcon>
          </DropdownHeader>
          <DropdownContent $isOpen={isOpen}>
            {[...options].sort((a, b) => a.label.localeCompare(b.label)).map((option) => (
              <OptionItem key={option.value}>
                <ToggleContainer>
                  <ToggleButton
                    $active={isOptionSelected(option.value, 'include')}
                    $type="include"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleToggle(option.value, 'include')
                    }}
                  >
                    Include
                  </ToggleButton>
                  <ToggleButton
                    $active={isOptionSelected(option.value, 'exclude')}
                    $type="exclude"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleToggle(option.value, 'exclude')
                    }}
                  >
                    Exclude
                  </ToggleButton>
                </ToggleContainer>
                <OptionLabel $selected={isOptionActive(option.value)}>
                  {option.label}
                </OptionLabel>
              </OptionItem>
            ))}
          </DropdownContent>
        </DropdownContainer>
      </Wrapper>
    </Container>
  )
}

export default FilterSectionWithToggle
